import React, {useCallback, useEffect, useState} from "react";
import Select from "react-select";
import {atom, useAtom} from "jotai";
import styled from "styled-components";
import Table from "../../components/table";
import {dataTotalInfo} from "../../components/common/entity";
import {selAdminList} from "../../services/platform/ManageAdminAxios";
import {columnAdminData, searchAccountInfo, selectAccountUseInfo} from "./entity/common";
import CreateAdminModal from "../../components/common/CreateAdminModal";
import PlatformSectionHeader from "./PlatformSectionHeader";
import PlatformTableBody from "./PlatformTableBody";
import SelectAdminModal from "../../components/common/SelectAdminModal";

const AdminListAtom=atom([]);
const selectStyle={
  control:(b,s)=>({...b,minHeight:42,height:42,borderColor:s.isFocused?"var(--color-primary)":"#dfe3e7",borderRadius:8,boxShadow:s.isFocused?"0 0 0 3px rgba(59,130,246,.1)":"none",fontSize:11,"&:hover":{borderColor:"var(--color-primary-border)"}}),
  valueContainer:b=>({...b,height:42,padding:"0 12px"}),indicatorsContainer:b=>({...b,height:42}),indicatorSeparator:()=>({display:"none"}),
  menu:b=>({...b,zIndex:40,overflow:"hidden",borderRadius:8}),option:(b,s)=>({...b,background:s.isSelected?"var(--color-primary-soft)":s.isFocused?"var(--color-surface-muted)":"var(--color-surface)",color:s.isSelected?"var(--color-primary-strong)":"#555",fontSize:11})
};

export default function PlatformAdmin(){
  const [open,setOpen]=useState(false);
  const [search,setSearch]=useState({...searchAccountInfo});
  const [list,setList]=useAtom(AdminListAtom);
  const [total,setTotal]=useState(dataTotalInfo);
  const fetchList=useCallback(async condition=>{
    const response=await selAdminList(condition);
    setList(response?.content||[]);
    setTotal({totalCount:response?.totalElements||0,totalPages:response?.totalPages||0,currentPage:response?.pageNumber||0});
  },[setList]);
  useEffect(()=>{fetchList(searchAccountInfo);},[fetchList]);
  const reset=()=>{const next={...searchAccountInfo};setSearch(next);fetchList(next);};
  const adminColumns=[
    {
      ...columnAdminData.find(column=>column.name==="status"),
      header:"사용 여부",
      width:100,
      render:({value})=><StatusTag $active={value==="NORMAL"}>{value==="NORMAL"?"활성화":"비활성화"}</StatusTag>
    },
    ...columnAdminData.filter(column=>column.name!=="status").map(column=>column.name==="email"?{
      ...column,
      render:({value,data})=><SelectAdminModal formType="admin" title="관리자 상세 정보" buttonText={value} data={data} onClick={()=>fetchList(search)}/>
    }:column)
  ].filter(Boolean);
  const openDetail=(...args)=>{
    const event=args.find(arg=>arg?.target||arg?.nativeEvent?.target||arg?.event?.target);
    const target=event?.target||event?.nativeEvent?.target||event?.event?.target;
    if(!target?.closest||target.closest("button,a,input,select,[role='button']")) return;
    target.closest(".InovuaReactDataGrid__row")?.querySelector("button")?.click();
  };
  const action=<AddAction><CreateAdminModal formType="admin" title="관리자 추가" buttonText="+ 관리자 추가"/></AddAction>;
  return <Page>
    <Card $open={open}>
      <PlatformSectionHeader title="조회 조건" description="계정 활성화 여부를 기준으로 관리자 목록을 조회합니다." collapsible open={open} onToggle={()=>setOpen(v=>!v)}/>
      {open&&<ConditionBody><Field><Label>활성화 여부</Label><Select styles={selectStyle} options={selectAccountUseInfo} value={selectAccountUseInfo.find(v=>v.value===search.activeYn)||selectAccountUseInfo[0]} onChange={v=>setSearch(p=>({...p,activeYn:v.value}))} isSearchable={false}/></Field>
        <Footer><span>조건을 설정한 뒤 적용 버튼을 눌러 목록을 조회합니다.</span><Actions><ResetButton onClick={reset}>초기화</ResetButton><ApplyButton onClick={()=>fetchList(search)}>적용</ApplyButton></Actions></Footer>
      </ConditionBody>}
    </Card>
    <Card><PlatformSectionHeader title="관리자 목록" description="등록된 관리자 정보와 계정 상태를 확인합니다." count={total.totalCount||0} unit="명" action={action}/>
      <PlatformTableBody><Table columns={adminColumns} data={list||[]} onRowClick={openDetail}/></PlatformTableBody>
    </Card>
  </Page>;
}
const Page=styled.div`display:flex;width:100%;flex-direction:column;gap:16px;`;
const Card=styled.section`position:relative;z-index:${({$open})=>$open?20:1};overflow:${({$open})=>$open?"visible":"hidden"};border:1px solid var(--color-border);border-radius:10px;background:var(--color-surface);box-shadow:0 5px 18px rgba(31,41,55,.045);`;
const ConditionBody=styled.div`padding:18px 20px 16px;`;
const Field=styled.div`display:flex;width:280px;max-width:100%;flex-direction:column;gap:7px;`;
const Label=styled.div`color:#444;font-size: var(--font-size-13);font-weight:700;`;
const Footer=styled.div`display:flex;align-items:center;justify-content:space-between;margin-top:18px;padding-top:14px;border-top:1px solid var(--color-divider);color:#929ba5;font-size: var(--font-size-11);`;
const Actions=styled.div`display:flex;gap:8px;`;
const ResetButton=styled.button`height:38px;padding:0 16px;border:1px solid #dfe3e7;border-radius:7px;background:var(--color-surface);color:#69737e;font-size: var(--font-size-13);font-weight:650;cursor:pointer;`;
const ApplyButton=styled(ResetButton)`border-color:var(--color-primary-strong);background:var(--color-primary-strong);color:var(--color-surface);`;
const AddAction=styled.div`button{min-width:104px;height:40px;padding:0 16px;border:1px solid var(--color-primary-border)!important;border-radius:7px!important;background:var(--color-primary-soft)!important;color:var(--color-primary-strong)!important;font-size: var(--font-size-13)!important;font-weight:700!important;box-shadow:none!important;}button:hover{background:var(--color-primary-muted)!important;}`;
const StatusTag=styled.span`
  display:inline-flex;min-width:54px;height:23px;align-items:center;justify-content:center;padding:0 9px;
  border:1px solid ${({$active})=>$active?"#b7e4cf":"#f2c2c2"};border-radius:12px;
  background:${({$active})=>$active?"#ecf8f2":"#fff1f1"};color:${({$active})=>$active?"#16845b":"var(--color-danger-strong)"};
  font-size: var(--font-size-12);font-weight:700;
`;
